import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SolidButton } from 'dbi-pearl-ui-kit';
import { VendorAuthService } from 'lib/apis/VendorAuthService';
import {
    ActionsRow,
    CodeFieldColumn,
    CodeInput,
    CodeInputWrap,
    ErrorText,
    FieldLabel,
    HintText,
    ResendButton,
    VerifyButtonWrap,
    VerifyDescription,
    VerifyTitle,
} from './EmailVerificationPanel.styled';

const OTP_LENGTH = 6;

/**
 * Pearl Connect verify-email UI (Figma modal)
 * @param {boolean} [constrainCodeFieldWidth=true]
 */
const EmailVerificationPanel = ({ email, onVerified, constrainCodeFieldWidth = true }) => {
    const [code, setCode] = useState('');
    const [emailOTPError, setEmailOTPErrror] = useState('');
    const vendorAuthService = VendorAuthService();
    const { t } = useTranslation();
    const [isResendDisabled, setIsResendDisabled] = useState(true);
    const [timer, setTimer] = useState(60);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const inputRef = useRef(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    useEffect(() => {
        if (isResendDisabled && timer > 0) {
            const countdown = setInterval(() => {
                setTimer((prev) => {
                    if (prev <= 1) {
                        clearInterval(countdown);
                        setIsResendDisabled(false);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(countdown);
        }
    }, [isResendDisabled, timer]);

    useEffect(() => {
        setIsButtonDisabled(code.length !== OTP_LENGTH);
    }, [code]);

    const handleCodeChange = (e) => {
        const value = e.target.value;
        if (/^[0-9]*$/.test(value)) {
            setCode(value.slice(0, OTP_LENGTH));
        } else if (value === '') {
            setCode('');
        }
    };

    const handleVerifyBtn = async () => {
        try {
            const results = await vendorAuthService.verifyEmailOTP({
                email,
                otp: code,
            });
            if (results?.ok && onVerified) {
                onVerified();
            }
        } catch (e) {
            console.error('Email OTP verification failed');
            setEmailOTPErrror(e);
        }
    };

    const handleResendEmailOTP = async () => {
        if (!isResendDisabled) {
            setIsResendDisabled(true);
            setTimer(60);
            setEmailOTPErrror('');
            setCode('');

            try {
                await vendorAuthService.resendEmailOTP(email);
            } catch (error) {
                setEmailOTPErrror('Failed to resend Email OTP');
                console.error('>> Failed to resend Email OTP:', error);
            }
        }
    };

    const resendLabel =
        isResendDisabled && timer > 0
            ? `${t('Forms.BasicDetails.VerificationModalContent.ResendOTP')} (${timer}s)`
            : t('Forms.BasicDetails.VerificationModalContent.ResendOTP');

    return (
        <>
            <VerifyTitle>{t('Forms.BasicDetails.VerificationModalContent.Title')}</VerifyTitle>
            <VerifyDescription>
                {t('Forms.BasicDetails.VerificationModalContent.Description', { email })}
            </VerifyDescription>
            <CodeFieldColumn $constrain={constrainCodeFieldWidth}>
                <FieldLabel htmlFor="vendor-email-verification-code">
                    {t('Forms.BasicDetails.VerificationModalContent.VerificationCodeLabel')}
                </FieldLabel>
                <CodeInputWrap $hasError={Boolean(emailOTPError)}>
                    <CodeInput
                        ref={inputRef}
                        id="vendor-email-verification-code"
                        data-testid="otp-input-id"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder={t(
                            'Forms.BasicDetails.VerificationModalContent.VerificationCodePlaceholder'
                        )}
                        value={code}
                        onChange={handleCodeChange}
                        maxLength={OTP_LENGTH}
                    />
                </CodeInputWrap>
            </CodeFieldColumn>
            {emailOTPError && <ErrorText>{emailOTPError}</ErrorText>}
            {isResendDisabled && timer > 0 && (
                <HintText>
                    {t('Forms.BasicDetails.VerificationModalContent.TooltipContent')}
                </HintText>
            )}
            <ActionsRow>
                <VerifyButtonWrap>
                    <SolidButton disabled={isButtonDisabled} onClick={handleVerifyBtn}>
                        {t('Forms.BasicDetails.VerificationModalContent.Verify')}
                    </SolidButton>
                </VerifyButtonWrap>
                <ResendButton
                    type="button"
                    $cooldown={isResendDisabled}
                    disabled={isResendDisabled}
                    onClick={handleResendEmailOTP}
                >
                    {resendLabel}
                </ResendButton>
            </ActionsRow>
        </>
    );
};

export default EmailVerificationPanel;
